import {
  ErrorCode,
  type ModuleKey,
  type Permission,
  permissionModule,
  type SessionInfo,
} from '@sahab/contracts'
import { ApiError } from '../lib/api'
import { allows, moduleEnabled, type Requirement } from './access'

/**
 * The refusal for a screen the session cannot open for want of `missing` (UX only, the API enforces the
 * same). When every missing permission belongs to a module the company has switched off, the page says
 * the feature is turned off (403 MODULE_DISABLED, as the API answers) instead of sending the user to
 * their administrator for a permission their roles already grant or cannot use (audit L13).
 */
export function refusal(session: SessionInfo, missing: readonly Permission[]): ApiError {
  const moduleOff = (p: Permission) => {
    const module = permissionModule(p)
    return module !== null && !moduleEnabled(session.modules, module)
  }
  return missing.length > 0 && missing.every(moduleOff)
    ? new ApiError({ status: 403, code: ErrorCode.MODULE_DISABLED })
    : new ApiError({ status: 403, code: ErrorCode.FORBIDDEN })
}

/**
 * Route-level permission check (UX only — the API enforces the same permission). A missing
 * permission renders the route's error state ("You don't have permission…", or "turned off for your
 * company" when its module is off) instead of a page that would only fail request by request.
 */
export function requirePermission(session: SessionInfo, permission: Permission): void {
  if (!session.permissions.includes(permission)) throw refusal(session, [permission])
}

/** Like requirePermission, for screens that need every one of several permissions (`CREATE_ACCESS`). */
export function requireAccess(session: SessionInfo, required: Requirement): void {
  if (!allows(session.permissions, required)) {
    const all: readonly Permission[] = typeof required === 'string' ? [required] : required
    throw refusal(
      session,
      all.filter((p) => !session.permissions.includes(p)),
    )
  }
}

/** Like requirePermission, for screens any one of several permissions opens. */
export function requireAnyPermission(session: SessionInfo, ...permissions: Permission[]): void {
  if (!permissions.some((p) => session.permissions.includes(p))) throw refusal(session, permissions)
}

/**
 * A screen of a module the company does not have (D15): the same refusal the API gives its routes
 * (403 MODULE_DISABLED), so the page says the feature is off rather than failing request by request.
 */
export function requireModule(session: SessionInfo, module: ModuleKey): void {
  if (!moduleEnabled(session.modules, module)) {
    throw new ApiError({ status: 403, code: ErrorCode.MODULE_DISABLED })
  }
}

/**
 * While an administrator-set password is pending a change, the only page the user may open is the
 * change-password page (the API refuses everything else with 403 PASSWORD_CHANGE_REQUIRED).
 */
export function mustChangePasswordRedirect(session: SessionInfo, pathname: string): boolean {
  return session.user.mustChangePassword && pathname !== '/change-password'
}
